import React, { useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { signup } from '../services/auth.services';
import { useAuthentication } from '../services/auth.provider';
import * as HttpStatus from 'http-status-codes';
import { useNavigate } from 'react-router';
import logo from '../../../assets/images/logo.svg';
import portrait from '../../../assets/images/portrait.jpg';
import { Button, FormControl, FormControlLabel, Grid2, Radio, RadioGroup } from '@mui/material';
import { makeStyles } from '@mui/styles';
import theme from '../../../core/theme';
import { UserFormValues } from '../../user/models/user';
import { capitalize } from 'lodash';
import { UserRoles } from '../../shared/constants';

const useStyles = makeStyles({
  container: {
    width: '1500px',
    height: '650px',
  },
  text: {
    fontSize: '16px',
    '& .highlightedText': {
      color: `${theme.palette.error.main} !important`,
    },
  },
  button: {
    width: '135px',
    height: '38px',
    backgroundColor: '#F67570 !important',
  },
  radioBtn: {
    '& span': {
      '& svg': {
        fill: '#E1E3E8 !important',
      },
    },
    '& .Mui-checked': {
      '& svg': {
        fill: '#30343F !important',
      },
    },
  },
});

const SignUp: React.FC = () => {
  const classes = useStyles();
  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm<UserFormValues>({ mode: 'onBlur' });
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [roleId, setRoleId] = useState<number>();
  const auth = useAuthentication();
  const navigate = useNavigate();

  const onSubmit: SubmitHandler<UserFormValues> = async (form) => {
    setErrorMessage('');

    const payload: UserFormValues = {
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email,
      password: form.password,
      roleId: roleId ?? 0,
      username: form.username,
    };
    try {
      const user = await signup(payload);
      auth.login(user);
      window.location.href = '/dashboard';
    } catch (error) {
      navigate(`/login`);
      if (error?.response?.status === HttpStatus.NOT_FOUND) {
        setErrorMessage('Invalid user');
      } else {
        setErrorMessage('Please try again');
      }
    }
  };

  const handleRoleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRoleId(parseInt(e.target.value, 10));
  };

  const handleLoginClick = () => {
    navigate(`/login`);
  };

  return (
    <Grid2 container direction="row" className={classes.container} justifyContent="center" alignContent="center">
      <Grid2 container mr={12} direction="column">
        <img src={logo} width="145px" height="145px" alt="appLogo" />
        <Grid2 container mb={6} direction="column">
          <Grid2 mb={2} mt={3}>
            <h1>Create new Account</h1>
          </Grid2>
          <span className={classes.text}>Welcome!</span>

          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid2 container justifyContent="start">
              <div className="text-danger mb-1 pb-5 text-center">{errorMessage}</div>
            </Grid2>

            <Grid2 container mt={1}>
              <Grid2 container direction="column" mr={2}>
                <label htmlFor="inputFirstName" className={classes.text}>
                  First Name
                  <input
                    {...register('firstName', {
                      required: 'First name is Required',
                    })}
                    name="firstName"
                    id="inputFirstName"
                    className={`form-control ${errors.firstName && 'is-invalid'}`}
                    placeholder="Enter your First Name"
                  />
                  {errors.firstName && <span>{errors.firstName.message}</span>}
                </label>
              </Grid2>

              <Grid2 container direction="column">
                <label htmlFor="inputLastName" className={classes.text}>
                  Last Name
                  <input
                    {...register('lastName', {
                      required: 'Last name is Required',
                    })}
                    name="lastName"
                    id="inputLastName"
                    className={`form-control ${errors.lastName && 'is-invalid'}`}
                    placeholder="Enter your Last Name"
                  />
                  {errors.lastName && <span>{errors.lastName.message}</span>}
                </label>
              </Grid2>
            </Grid2>

            <Grid2 container mt={1} direction="column">
              <label htmlFor="inputEmail" className={classes.text}>
                Email Address
                <input
                  {...register('email', {
                    required: 'Email is Required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}|[A-Z0-9_-]$/i,
                      message: 'Invalid email address',
                    },
                  })}
                  name="email"
                  id="inputEmail"
                  className={`form-control ${errors.email && 'is-invalid'}`}
                  placeholder="Enter your Email Address"
                />
                {errors.email && <span>{errors.email.message}</span>}
              </label>
            </Grid2>

            <Grid2 container mt={1} direction="column">
              <label htmlFor="inputPassword" className={classes.text}>
                Set Password
                <input
                  {...register('password', { required: 'Password is required' })}
                  type="password"
                  name="password"
                  id="inputPassword"
                  className={`form-control ${errors.password && 'is-invalid'}`}
                  placeholder="Password"
                />
                {errors.password && <span>{errors.password.message}</span>}
              </label>
            </Grid2>

            <Grid2 container mt={1} direction="column">
              <label htmlFor="inputUsername" className={classes.text}>
                Username
                <input
                  {...register('username', {
                    required: 'Username is Required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}|[A-Z0-9_-]$/i,
                      message: 'Invalid Username',
                    },
                  })}
                  name="username"
                  id="inputUsername"
                  className={`form-control ${errors.username && 'is-invalid'}`}
                  placeholder="Enter your Username"
                />
                {errors.username && <span>{errors.username.message}</span>}
              </label>
            </Grid2>

            <Grid2 container mt={1} direction="column">
              <label>Role</label>
              <FormControl>
                <RadioGroup
                  aria-labelledby="demo-controlled-radio-buttons-group"
                  name="controlled-radio-buttons-group"
                  value={roleId}
                  onChange={handleRoleChange}
                >
                  <Grid2 container direction="column">
                    <Grid2 container direction="row">
                      {UserRoles.slice(0, 2).map((r) => (
                        <FormControlLabel
                          sx={{ ml: '4px' }}
                          className={classes.radioBtn}
                          value={r.id}
                          control={<Radio />}
                          label={capitalize(r.name)}
                        />
                      ))}
                    </Grid2>
                    <Grid2 container direction="row">
                      {UserRoles.slice(2).map((r) => (
                        <FormControlLabel
                          sx={{ ml: '4px' }}
                          className={classes.radioBtn}
                          value={r.id}
                          control={<Radio />}
                          label={capitalize(r.name)}
                        />
                      ))}
                    </Grid2>
                  </Grid2>
                </RadioGroup>
              </FormControl>
            </Grid2>
            <Grid2 container justifyContent="start" mt={2}>
              <Button
                variant="contained"
                type="submit"
                className={classes.button}
                sx={{ textTransform: 'none !important' }}
              >
                Create Account
              </Button>
            </Grid2>
          </form>
        </Grid2>
        <Grid2 container justifyContent="center">
          <span style={{ paddingTop: '16px' }}>
            Already have an account?
            <Button onClick={handleLoginClick} role="button">
              &nbsp;<span style={{ color: '#F67570', textTransform: 'none' }}>Login Here</span>
            </Button>
          </span>
        </Grid2>
      </Grid2>
      <Grid2 container alignContent="center">
        <img src={portrait} width="480" height="638" alt="sideImage" />
      </Grid2>
    </Grid2>
  );
};

export default SignUp;
